import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { useEntitlementGate } from '../contexts/EntitlementGateContext';
import { useAPI } from "../contexts/APIContext";
import HelpFooter from "../components/HelpFooter";
import { useAnalytics, usePageTracking } from "../hooks/useAnalytics";
import logger from "../utils/logger";
import ReferralSettingsSection from "../components/ReferralSettingsSection";
import StartMembershipNow from '../components/StartMembershipNow';
import SubscriptionModal from "../components/SubscriptionModal";
import CancelFlow from "../components/CancelFlow";
import LikedContentCard from "../components/cards/LikedContentCard";
import { reportCheckoutReturn } from "../utils/googleAdsConversion";

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2 } },
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.06 } },
};

const fadeInUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

const Settings = () => {
  usePageTracking("Settings");
  const {
    user,
    signOut,
    updateUser,
    refreshUserData,
    subscriptionStatus,
    costs,
    syncAndRefreshSubscription,
    refreshSubscriptionStatus,
    waitForSubscriptionActivation,
  } = useAuth();
  const { openWall, cutoverLive } = useEntitlementGate();
  const { hasEntitlement } = useAuth();
  const { apiClient } = useAPI();
  const { trackSubscriptionEvent } = useAnalytics();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("profile");
  const [nowTs, setNowTs] = useState(() => Date.now()); // ticks the star-refill countdown
  const [isLoading, setIsLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [likedLoading, setLikedLoading] = useState(false);
  const [likedItems, setLikedItems] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uploadingPfp, setUploadingPfp] = useState(false);
  const [pfpPreview, setPfpPreview] = useState(null);
  const [pfpFile, setPfpFile] = useState(null);
  const [showPfpConfirm, setShowPfpConfirm] = useState(false);
  const [pfpStatus, setPfpStatus] = useState("");
  const pfpInputRef = useRef(null);
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [highlightTier, setHighlightTier] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Tick the star-refill countdown once a minute so it stays live.
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Personalization states
  const [personalizationShows, setPersonalizationShows] = useState([]);
  const [selectedShows, setSelectedShows] = useState([]);
  const [personalizationLoading, setPersonalizationLoading] = useState(false);
  const [personalizationSaving, setPersonalizationSaving] = useState(false);

  // Interests states
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [customInterest, setCustomInterest] = useState("");
  const [interestsSaving, setInterestsSaving] = useState(false);

  // Shop states
  const [shopData, setShopData] = useState({
    starAmounts: {
      first_tier: null,
      second_tier: null,
      third_tier: null,
      fourth_tier: null,
    },
    stripeProductIds: {
      first_tier: "",
      second_tier: "",
      third_tier: "",
      fourth_tier: "",
    },
  });
  const [shopLoading, setShopLoading] = useState(false);
  const [shopError, setShopError] = useState(null);
  const [starDeliveryAmount, setStarDeliveryAmount] = useState(50); // Default to 50 for free tier

  // Gift state
  const GIFT_PLANS = [
    { id: "1_month", label: "1 Month Access", desc: "The perfect spark of inspiration", price: "$15", months: 1, popular: false },
    { id: "3_months", label: "3 Months Journey", desc: "Deep storytelling exploration", price: "$45", oldPrice: "$55", months: 3, popular: true },
    { id: "6_months", label: "6 Months Odyssey", desc: "Complete narrative mastery", price: "$90", oldPrice: "$110", months: 6, popular: false },
  ];
  const [giftRecipientName, setGiftRecipientName] = useState("");
  const [giftRecipientEmail, setGiftRecipientEmail] = useState("");
  const [giftSenderName, setGiftSenderName] = useState("");
  const [giftPersonalMessage, setGiftPersonalMessage] = useState("");
  const [giftSelectedPlanId, setGiftSelectedPlanId] = useState("1_month");
  const [giftSubmitting, setGiftSubmitting] = useState(false);
  const giftSelectedPlan = useMemo(
    () => GIFT_PLANS.find((plan) => plan.id === giftSelectedPlanId) || null,
    [giftSelectedPlanId]
  );
  const giftContinueDisabled = !giftRecipientName.trim() || !giftRecipientEmail.trim() || !giftSelectedPlan || giftSubmitting;

  const handleGiftContinue = async (e) => {
    e.preventDefault();
    if (!user?.id || !user?.email) { showToast("Please log in to continue.", "error"); return; }
    if (!giftSelectedPlan) { showToast("Please select a plan duration.", "error"); return; }
    const payload = {
      type: "gift",
      user_id: user.id,
      user_email: user.email,
      recipient_email: giftRecipientEmail.trim(),
      recipient_name: giftRecipientName.trim(),
      duration_months: giftSelectedPlan.months,
      success_url: `${window.location.origin}/settings?tab=gift&gift_success=true`,
      cancel_url: `${window.location.origin}/settings?tab=gift`,
    };
    setGiftSubmitting(true);
    try {
      const response = await apiClient.request("/create-stripe-checkout-session", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (response?.status && response?.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }
      showToast(response?.error || "Failed to create checkout session. Please try again.", "error");
    } catch (error) {
      showToast("Failed to redirect to checkout. Please try again.", "error");
    } finally {
      setGiftSubmitting(false);
    }
  };

  // Form states
  const [profileForm, setProfileForm] = useState({
    username: user?.username || user?.name || "",
  });

  // Real stats data from API
  const [stats, setStats] = useState({
    charactersCount: 0,
    showsCount: 0,
    storiesCount: 0,
  });

  // Load user stats on component mount and handle tab switching
  useEffect(() => {
    loadUserStatsAndLimits();

    // Check for tab parameter and switch to it
    const tab = searchParams.get("tab");
    console.log("🔍 Active tab from URL:", tab);
    if (
      tab &&
      ["profile", "stats", "shop", "referral", "gift", "liked", "shows", "interests", "account"].includes(tab)
    ) {
      console.log("✅ Setting active tab to:", tab);
      setActiveTab(tab);
      // Only clear the tab parameter if there are no other important params (like purchased)
      const purchased = searchParams.get("purchased");
      if (!purchased) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("tab");
        setSearchParams(newParams);
      }
    }

    // Check for plan parameter to auto-open subscription modal with a plan highlighted
    const plan = searchParams.get("plan");
    if (plan && ["starter", "plus", "family", "unlimited"].includes(plan)) {
      setHighlightTier(plan);
      setShowSubscriptionModal(true);
      setActiveTab("shop");
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("plan");
      setSearchParams(newParams);
    }

    // Check for upgrade parameter and refresh subscription data
    const upgraded = searchParams.get("upgraded");
    const cancelled = searchParams.get("cancelled");
    const purchased = searchParams.get("purchased");

    if (upgraded === "true") {
      // PostHog subscription_purchased fires server-side from Stripe webhook (no client-side to avoid double-counting)

      // Google Ads conversion: only a Stripe session our server confirms paid,
      // at what was charged, keyed on the session (utils/googleAdsConversion)
      reportCheckoutReturn(apiClient, {
        sessionId: searchParams.get("session_id"),
        tier: searchParams.get("tier") || "",
        billing: searchParams.get("billing") || "monthly",
        email: user?.email,
      });

      showToast(
        "Payment successful! Your subscription has been updated.",
        "success"
      );
      // Clear the parameter from URL
      setSearchParams({});
      // Poll until RevenueCat has ingested the new subscription (Stripe->RC
      // ingestion can lag the redirect), then refresh state past all caches
      waitForSubscriptionActivation();
    } else if (purchased === "true") {
      // PostHog stars_purchased fires server-side from Stripe webhook (no client-side to avoid double-counting)

      // Google Ads conversion — star purchase with dynamic value
      if (window.gtag) {
        const productId = searchParams.get("product") || "";
        // Map product IDs to prices (match your Stripe products)
        const productPrices = {
          "price_1T9DlKLEUTw6WZP2OFWoUmnS": 4.99,   // 75 Pixley Stars
          "price_1T9DlhLEUTw6WZP2HltYYkx0": 9.99,   // 175 Pixley Stars
          "price_1T9DluLEUTw6WZP2ZVJOFsYp": 19.99,  // 400 Pixley Stars
          "price_1T9Dm6LEUTw6WZP29xymPS8M": 49.99,   // 1000 Pixley Stars
        };
        const value = productPrices[productId] || 9.99;
        window.gtag('event', 'conversion', {
          send_to: 'AW-17614735089/1FEiCNDzyJ0cEPGNrs9B',
          value: value,
          currency: 'USD',
          transaction_id: `stars_${user?.id}_${Date.now()}`,
        });
        if (user?.email) {
          window.gtag('set', 'user_data', { email: user.email });
        }
      }

      showToast(
        "Payment successful! Your stars have been added to your account.",
        "success"
      );
      // Clear the purchased parameter from URL but keep tab if it's shop
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("purchased");
      if (newParams.get("tab") === "shop") {
        // Keep tab=shop in URL
        setSearchParams(newParams);
      } else {
        // Remove tab if it's not shop
        newParams.delete("tab");
        setSearchParams(newParams);
      }
      // Refresh user data to get updated star count
      if (user?.id && apiClient) {
        setTimeout(async () => {
          try {
            const sessionResponse = await apiClient.getSession();
            if (sessionResponse.status && sessionResponse.data?.user) {
              updateUser(sessionResponse.data.user);
            }
          } catch (error) {
            logger.error("Error refreshing user data after purchase:", error);
          }
        }, 1000);
      }
    } else if (cancelled === "true") {
      showToast("Payment was cancelled.", "info");
    } else if (searchParams.get("gift_success") === "true") {
      showToast("🎉 Gift sent successfully! The recipient will receive an email shortly.", "success");
      setGiftRecipientName("");
      setGiftRecipientEmail("");
      setGiftSenderName("");
      setGiftPersonalMessage("");
      setGiftSelectedPlanId("1_month");
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("gift_success");
      setSearchParams(nextParams, { replace: true });
      // Clear the parameter from URL
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, apiClient]);

  // Load personalization shows when switching to shows tab
  useEffect(() => {
    if (activeTab === "shows") {
      loadPersonalizationShows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load interests when switching to interests tab
  useEffect(() => {
    if (activeTab === "interests" && personalizationShows.length === 0) {
      // Load persona data if not already loaded
      loadPersonalizationShows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load star shop products when switching to shop tab
  useEffect(() => {
    if (activeTab === "shop") {
      loadStarShopProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Update form when user changes
  useEffect(() => {
    setProfileForm({
      username: user?.username || user?.name || "",
    });
  }, [user]);

  const loadUserStatsAndLimits = async () => {
    if (!user?.id || !apiClient) return;

    try {
      setStatsLoading(true);

      // Only load stats (subscription data comes from AuthContext)
      const statsResponse = await apiClient.getUserStats(user.id);

      // Handle stats response
      if (statsResponse.status) {
        setStats(statsResponse.data);
      } else {
        logger.error("Failed to load user stats:", statsResponse.error);
        if (
          !statsResponse.error?.includes("401") &&
          !statsResponse.error?.includes("Unauthorized")
        ) {
          setError("Failed to load statistics");
        }
      }
    } catch (error) {
      logger.error("Error loading user data:", error);
      // Don't show error for authentication issues - user will be redirected to login
      if (
        !error.message?.includes("401") &&
        !error.message?.includes("Unauthorized")
      ) {
        setError("Failed to load user data");
      }
    } finally {
      setStatsLoading(false);
    }
  };

  const loadStarShopProducts = async () => {
    if (!apiClient || !user?.id) return;

    try {
      setShopLoading(true);
      setShopError(null);

      // Fetch star shop products and costs in parallel
      const [productsResponse, costsResponse] = await Promise.all([
        apiClient.getStarShopProducts(),
        apiClient.getCosts(user.id, subscriptionStatus?.subscriptionType || "NONE"),
      ]);

      // Handle star shop products
      if (productsResponse.status && productsResponse.data?.tiers) {
        const tiers = productsResponse.data.tiers;
        const newShopData = {
          starAmounts: {
            first_tier: tiers.first_tier?.stars ?? null,
            second_tier: tiers.second_tier?.stars ?? null,
            third_tier: tiers.third_tier?.stars ?? null,
            fourth_tier: tiers.fourth_tier?.stars ?? null,
          },
          stripeProductIds: {
            first_tier: tiers.first_tier?.product_id || "",
            second_tier: tiers.second_tier?.product_id || "",
            third_tier: tiers.third_tier?.product_id || "",
            fourth_tier: tiers.fourth_tier?.product_id || "",
          },
        };

        setShopData(newShopData);
        logger.log("✅ Loaded star shop products:", newShopData);
      } else {
        const errorMsg = productsResponse.error || "Failed to load star shop products";
        logger.error("Failed to load star shop products:", errorMsg);
        setShopError(errorMsg);
      }

      // Handle star delivery amount from costs
      if (costsResponse.status && costsResponse.data?.rate_limits?.star_delivery) {
        setStarDeliveryAmount(costsResponse.data.rate_limits.star_delivery);
        logger.log("✅ Loaded star delivery amount:", costsResponse.data.rate_limits.star_delivery);
      } else {
        logger.warn("Failed to load star delivery amount, using default");
        // Keep default value (50)
      }
    } catch (error) {
      logger.error("Error loading star shop products:", error);
      setShopError(error.message || "Failed to load shop products");
    } finally {
      setShopLoading(false);
    }
  };

  const handleUpgrade = () => {
    setShowSubscriptionModal(true);
  };

  const handleStarPurchase = async (tier) => {
    logger.log("🛒 handleStarPurchase called with tier:", tier);
    logger.log("🛒 Current shopData:", shopData);
    logger.log("🛒 User:", { id: user?.id, email: user?.email });
    
    if (!user?.id || !user?.email) {
      logger.error("🛒 Missing user data");
      showToast(
        "Unable to process purchase. Please try logging out and back in.",
        "error"
      );
      return;
    }

    const productId = shopData.stripeProductIds[tier];
    logger.log("🛒 Product ID for tier:", { tier, productId });
    
    if (!productId) {
      logger.error("🛒 Product ID not found for tier:", tier);
      showToast("Product not loaded. Please wait a moment and try again.", "error");
      return;
    }

    try {
      showToast("Redirecting to secure checkout...", "info");

      logger.log("🛒 Initiating star purchase:", {
        tier,
        productId,
        userId: user.id,
        userEmail: user.email,
      });

      const response = await apiClient.createStripeStarPurchaseSession(
        user.id,
        user.email,
        productId
      );

      logger.log("🛒 RevenueCat checkout response:", response);

      if (response.status && response.data?.checkout_url) {
        // Redirect to RevenueCat Checkout
        logger.log("🛒 Redirecting to checkout URL:", response.data.checkout_url);
        window.location.href = response.data.checkout_url;
      } else {
        logger.error("❌ Failed to create checkout session:", response);
        const errorMsg = response.error || response.message || "Failed to create checkout session";
        showToast(errorMsg, "error");
        throw new Error(errorMsg);
      }
    } catch (error) {
      logger.error("Error creating star purchase checkout session:", error);
      const errorMessage = error.message || error.response?.data?.detail || "Failed to redirect to checkout. Please try again.";
      showToast(errorMessage, "error");
    }
  };

  const handleManageSubscription = async () => {
    if (!user?.id) {
      showToast(
        "Unable to access subscription management. Please try logging out and back in.",
        "error"
      );
      return;
    }

    try {
      showToast("Redirecting to subscription management...", "info");

      const response = await apiClient.createStripePortalSession(user.id);

      if (response.status && response.data.portal_url) {
        // Redirect to RevenueCat Customer Portal
        window.location.href = response.data.portal_url;
      } else {
        throw new Error(response.error || "Failed to create portal session");
      }
    } catch (error) {
      logger.error("Error creating portal session:", error);
      if (error.message?.includes("No Stripe subscription found") || error.message?.includes("No subscription found")) {
        showToast(
          "This subscription is not managed by Stripe. App Store subscriptions must be managed through Apple.",
          "info"
        );
      } else {
        showToast(
          "Failed to access subscription management. Please try again.",
          "error"
        );
      }
    }
  };

  const handleProfilePictureSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showToast("Please select an image file.", "error");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast("Image must be under 10MB.", "error");
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    setPfpPreview(previewUrl);
    setPfpFile(file);
    setShowPfpConfirm(true);
    if (pfpInputRef.current) pfpInputRef.current.value = "";
  };

  const handlePfpConfirmUpload = async () => {
    if (!pfpFile || !user?.id || !apiClient) return;
    setShowPfpConfirm(false);
    setUploadingPfp(true);
    setPfpStatus("Uploading...");
    try {
      const formData = new FormData();
      formData.append("file", pfpFile);
      console.log("[PFP] Uploading to:", `/users/${user.id}/upload-profile-picture`);
      const resp = await apiClient.uploadFileToEndpoint(
        `/users/${user.id}/upload-profile-picture`,
        formData
      );
      console.log("[PFP] Upload response:", resp);
      if (resp?.status && resp?.data?.profile_picture) {
        setPfpStatus("Updated!");
        showToast("Profile picture updated!", "success");
        // Preload the new URL so browser has it cached before we clear preview
        const newUrl = resp.data.profile_picture;
        await new Promise((resolve) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = resolve;
          img.src = newUrl;
        });
        if (refreshUserData) await refreshUserData();
        setTimeout(() => { setPfpStatus(""); setPfpPreview(null); setPfpFile(null); }, 2000);
      } else if (resp?.success === false) {
        setPfpPreview(null);
        setPfpFile(null);
        setPfpStatus("");
        showToast(resp?.error || "Failed to upload. Please try again.", "error");
      } else {
        setPfpPreview(null);
        setPfpFile(null);
        setPfpStatus("");
        showToast("Failed to upload. Please try again.", "error");
      }
    } catch (err) {
      console.error("[PFP] Upload error:", err);
      setPfpPreview(null);
      setPfpFile(null);
      setPfpStatus("");
      showToast(err?.message || "Failed to upload. Please try again.", "error");
    } finally {
      setUploadingPfp(false);
    }
  };

  const handlePfpCancelUpload = () => {
    if (pfpPreview) URL.revokeObjectURL(pfpPreview);
    setPfpPreview(null);
    setPfpFile(null);
    setShowPfpConfirm(false);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id || !apiClient) return;

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await apiClient.updateUsername(
        user.id,
        profileForm.username
      );

      if (response.status) {
        // Update user data in auth context
        const updatedUser = { ...user, username: profileForm.username };
        updateUser(updatedUser);
        setSuccess("Username updated successfully!");

        // Show toast notification
        showToast("Username updated successfully!", "success");

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(response.error || "Failed to update username");
      }
    } catch (error) {
      logger.error("Error updating username:", error);
      setError("Failed to update username");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.id || !apiClient) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await apiClient.deleteUser(user.id);

      if (response.status) {
        // Account deleted successfully, sign out and redirect
        showToast("Account deleted successfully", "success");
        await signOut();
        navigate("/auth");
      } else {
        setError(response.error || "Failed to delete account");
        setIsLoading(false);
      }
    } catch (error) {
      logger.error("Error deleting account:", error);
      setError("Failed to delete account");
      setIsLoading(false);
    }
  };

  const loadPersonalizationShows = async () => {
    if (!apiClient || !user?.id) return;

    try {
      setPersonalizationLoading(true);

      // Load both shows and user's persona in parallel
      const [showsResponse, personaResponse] = await Promise.all([
        apiClient.getPersonalizationShows(),
        user.persona
          ? Promise.resolve({ status: true, data: { persona: user.persona } })
          : apiClient.getUserPersona(user.id),
      ]);

      if (showsResponse.status && showsResponse.data) {
        setPersonalizationShows(showsResponse.data);
      } else {
        logger.error(
          "Failed to load personalization shows:",
          showsResponse.error
        );
        showToast("Failed to load personalization shows", "error");
      }

      // Pre-populate selected shows and interests from user's persona
      if (personaResponse.status && personaResponse.data) {
        const persona = personaResponse.data.persona || user.persona;
        if (
          persona &&
          persona.favorite_shows &&
          Array.isArray(persona.favorite_shows)
        ) {
          setSelectedShows(persona.favorite_shows);
          logger.log("Pre-populated favorite shows:", persona.favorite_shows);
        }
        if (
          persona &&
          persona.aggregated_interests &&
          Array.isArray(persona.aggregated_interests)
        ) {
          setSelectedInterests(persona.aggregated_interests);
          logger.log("Pre-populated interests:", persona.aggregated_interests);
        }
      }
    } catch (error) {
      logger.error("Error loading personalization shows:", error);
      showToast("Failed to load personalization shows", "error");
    } finally {
      setPersonalizationLoading(false);
    }
  };

  // Auto-save shows with debounce
  const showsSaveTimeoutRef = useRef(null);
  const autoSaveShows = (newShows) => {
    if (showsSaveTimeoutRef.current) clearTimeout(showsSaveTimeoutRef.current);
    showsSaveTimeoutRef.current = setTimeout(async () => {
      if (!user?.id || !apiClient) return;
      try {
        const updatedPersona = {
          favorite_shows: newShows,
          aggregated_topics: user.persona?.aggregated_topics || [],
          aggregated_interests: selectedInterests,
          aggregated_preferences: user.persona?.aggregated_preferences || [],
        };
        await apiClient.updateUserPersona(user.id, updatedPersona);
        updateUser({ ...user, persona: updatedPersona });
      } catch (error) {
        logger.error("Auto-save shows failed:", error);
      }
    }, 800);
  };

  const toggleShowSelection = (showId) => {
    setSelectedShows((prev) => {
      const next = prev.includes(showId)
        ? prev.filter((id) => id !== showId)
        : [...prev, showId];
      autoSaveShows(next);
      return next;
    });
  };

  const getEmojiForInterest = (interest) => {
    // First check our comprehensive dictionary
    if (interestEmojis[interest]) {
      return interestEmojis[interest];
    }

    // Case-insensitive search
    const match = Object.keys(interestEmojis).find(
      (key) => key.toLowerCase() === interest.toLowerCase()
    );
    if (match) {
      return interestEmojis[match];
    }

    // Partial match search
    const partialMatch = Object.keys(interestEmojis).find(
      (key) =>
        interest.toLowerCase().includes(key.toLowerCase()) ||
        key.toLowerCase().includes(interest.toLowerCase())
    );
    if (partialMatch) {
      return interestEmojis[partialMatch];
    }

    // Category-based fallback
    const lowerInterest = interest.toLowerCase();

    const categories = [
      {
        keywords: [
          "dog",
          "cat",
          "horse",
          "bird",
          "fish",
          "pet",
          "zoo",
          "wildlife",
          "farm",
        ],
        emoji: "🐾",
      },
      {
        keywords: [
          "space",
          "star",
          "planet",
          "galaxy",
          "astronaut",
          "rocket",
          "moon",
          "sun",
        ],
        emoji: "🚀",
      },
      {
        keywords: [
          "art",
          "draw",
          "paint",
          "create",
          "design",
          "sketch",
          "craft",
        ],
        emoji: "🎨",
      },
      {
        keywords: ["music", "song", "sing", "band", "instrument", "dance"],
        emoji: "🎵",
      },
      {
        keywords: [
          "sport",
          "play",
          "ball",
          "game",
          "run",
          "jump",
          "swim",
          "bike",
          "exercise",
        ],
        emoji: "⚽️",
      },
      {
        keywords: [
          "learn",
          "school",
          "study",
          "read",
          "book",
          "math",
          "science",
          "history",
        ],
        emoji: "📚",
      },
      {
        keywords: [
          "tech",
          "computer",
          "phone",
          "app",
          "game",
          "robot",
          "digital",
        ],
        emoji: "💻",
      },
      {
        keywords: ["food", "eat", "cook", "bake", "recipe", "restaurant"],
        emoji: "🍕",
      },
    ];

    for (const category of categories) {
      if (
        category.keywords.some((keyword) => lowerInterest.includes(keyword))
      ) {
        return category.emoji;
      }
    }

    // Default sparkle emoji for unmatched custom interests
    return "⭐";
  };

  // Auto-save interests with debounce
  const interestsSaveTimeoutRef = useRef(null);
  const autoSaveInterests = (newInterests) => {
    if (interestsSaveTimeoutRef.current) clearTimeout(interestsSaveTimeoutRef.current);
    interestsSaveTimeoutRef.current = setTimeout(async () => {
      if (!user?.id || !apiClient) return;
      try {
        const updatedPersona = {
          favorite_shows: selectedShows,
          aggregated_topics: user.persona?.aggregated_topics || [],
          aggregated_interests: newInterests,
          aggregated_preferences: user.persona?.aggregated_preferences || [],
        };
        await apiClient.updateUserPersona(user.id, updatedPersona);
        updateUser({ ...user, persona: updatedPersona });
      } catch (error) {
        logger.error("Auto-save interests failed:", error);
      }
    }, 800);
  };

  const toggleInterest = (interest) => {
    setSelectedInterests((prev) => {
      const next = prev.includes(interest)
        ? prev.filter((i) => i !== interest)
        : [...prev, interest];
      autoSaveInterests(next);
      return next;
    });
  };

  const addCustomInterest = () => {
    const trimmed = customInterest.trim();
    if (trimmed && !selectedInterests.includes(trimmed)) {
      setSelectedInterests((prev) => {
        const next = [...prev, trimmed];
        autoSaveInterests(next);
        return next;
      });
      setCustomInterest("");
    }
  };

  const removeInterest = (interest) => {
    setSelectedInterests((prev) => prev.filter((i) => i !== interest));
  };

  const handleSavePersonalization = async () => {
    if (!user?.id || !apiClient) return;

    try {
      setPersonalizationSaving(true);

      // Build the updated persona object
      const updatedPersona = {
        favorite_shows: selectedShows,
        aggregated_topics: user.persona?.aggregated_topics || [],
        aggregated_interests: selectedInterests,
        aggregated_preferences: user.persona?.aggregated_preferences || [],
      };

      // Update persona via API
      const response = await apiClient.updateUserPersona(
        user.id,
        updatedPersona
      );

      if (response.status) {
        showToast("Personalization preferences saved successfully!", "success");

        // Update local user object with new persona data
        const updatedUser = {
          ...user,
          persona: updatedPersona,
        };
        updateUser(updatedUser);
      } else {
        showToast(response.error || "Failed to save preferences", "error");
      }
    } catch (error) {
      logger.error("Error saving personalization:", error);
      showToast("Failed to save preferences", "error");
    } finally {
      setPersonalizationSaving(false);
    }
  };

  const handleSaveInterests = async () => {
    if (!user?.id || !apiClient) return;

    try {
      setInterestsSaving(true);

      // Build the updated persona object
      const updatedPersona = {
        favorite_shows: selectedShows,
        aggregated_topics: user.persona?.aggregated_topics || [],
        aggregated_interests: selectedInterests,
        aggregated_preferences: user.persona?.aggregated_preferences || [],
      };

      // Update persona via API
      const response = await apiClient.updateUserPersona(
        user.id,
        updatedPersona
      );

      if (response.status) {
        showToast("Interests saved successfully!", "success");

        // Update local user object with new persona data
        const updatedUser = {
          ...user,
          persona: updatedPersona,
        };
        updateUser(updatedUser);
      } else {
        showToast(response.error || "Failed to save interests", "error");
      }
    } catch (error) {
      logger.error("Error saving interests:", error);
      showToast("Failed to save interests", "error");
    } finally {
      setInterestsSaving(false);
    }
  };

  const showToast = (message, type = "success") => {
    const event = new CustomEvent("showToast", {
      detail: { message, type },
    });
    window.dispatchEvent(event);
  };

  // Comprehensive interest emojis dictionary
  const interestEmojis = {
    // Core Categories
    Animals: "🐾",
    Space: "🚀",
    Dinosaurs: "🦕",
    Art: "🎨",
    Music: "🎵",
    Sports: "⚽️",
    Science: "🔬",
    Magic: "✨",
    Adventure: "🗺️",
    Fantasy: "🧚",
    Robots: "🤖",
    Nature: "🌿",
    Ocean: "🌊",
    Cooking: "👨‍🍳",
    Dancing: "💃",
    Reading: "📚",
    Building: "🏗️",
    Cars: "🚗",
    Superheroes: "🦸",
    Princess: "👸",
    Pirates: "🏴‍☠️",
    Knights: "⚔️",
    Fairies: "🧚‍♀️",
    Dragons: "🐉",
    Unicorns: "🦄",

    // Extended Categories
    Gaming: "🎮",
    Photography: "📸",
    Movies: "🎬",
    Books: "📖",
    Writing: "✍️",
    Travel: "✈️",
    Food: "🍕",
    Pets: "🐶",
    Gardening: "🌱",
    Fashion: "👗",
    Crafts: "🧵",
    Puzzles: "🧩",
    "Board Games": "🎲",
    "Video Games": "🕹️",
    Theater: "🎭",
    Comedy: "😂",
    History: "📜",
    Geography: "🗺️",
    Languages: "🗣️",
    Math: "🧮",
    Chemistry: "⚗️",
    Biology: "🧬",
    Physics: "⚛️",

    // Creative Arts
    Drawing: "🖊️",
    Painting: "🖌️",
    Singing: "🎤",
    Guitar: "🎸",
    Piano: "🎹",
    Drums: "🥁",
    Violin: "🎻",
    Ballet: "🩰",
    "Hip Hop": "🕺",
    Jazz: "🎺",

    // Nature & Outdoors
    Hiking: "🥾",
    Camping: "⛺",
    Swimming: "🏊",
    Fishing: "🎣",
    Birds: "🐦",
    Flowers: "🌺",
    Trees: "🌳",
    Mountains: "🏔️",
    Beach: "🏖️",

    // Sports & Activities
    Soccer: "⚽",
    Basketball: "🏀",
    Baseball: "⚾",
    Tennis: "🎾",
    Golf: "⛳",
    Running: "🏃",
    Cycling: "🚴",
    Skiing: "⛷️",
    Skateboarding: "🛹",
    Surfing: "🏄",
    Gymnastics: "🤸",
    "Martial Arts": "🥋",

    // Food & Cooking
    Baking: "🧁",
    "Ice Cream": "🍦",
    Pizza: "🍕",
    Sushi: "🍣",
    Fruits: "🍎",
    Vegetables: "🥕",
    Chocolate: "🍫",
    Candy: "🍬",
    Cake: "🎂",
    Cookies: "🍪",
    Smoothies: "🥤",
    Tea: "🍵",

    // Fantasy & Mythology
    Wizards: "🧙",
    Mermaids: "🧜",
    Angels: "👼",
    Vampires: "🧛",
    Zombies: "🧟",
    Ghosts: "👻",
    Monsters: "👹",
    Aliens: "👽",
    Ninjas: "🥷",
    Vikings: "⚔️",
    Mythology: "🏛️",

    AI: "🤖",
    Coding: "💻",
    Drones: "🚁",
    VR: "🥽",
    Computers: "🖥️",
    Internet: "🌐",
  };

  // Fetch liked items when tab becomes active
  useEffect(() => {
    if (activeTab !== "liked" || !apiClient) return;
    let cancelled = false;
    const fetchLiked = async () => {
      setLikedLoading(true);
      try {
        const response = await apiClient.getLikes();
        const list = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];
        const combined = [];
        for (const like of list) {
          const { content_type: contentType, content_id: contentId } = like;
          if (!contentId) continue;
          try {
            if (contentType === "storybook") {
              const res = await apiClient.getAudiobook(contentId);
              if (res?.status && res?.data) {
                const d = res.data;
                combined.push({
                  id: d.id ?? contentId, type: "storybook",
                  title: d.title ?? "Untitled Storybook", description: d.description ?? "",
                  thumbnail_url: d.thumbnail_url ?? d.cover_url ?? null, views: d.views ?? 0,
                });
              }
            } else if (contentType === "video") {
              const res = await apiClient.getStory(contentId);
              if (res?.status && res?.data) {
                const d = res.data;
                combined.push({
                  id: d.id ?? contentId, type: "video",
                  title: d.title ?? d.name ?? "Untitled Video", description: d.description ?? "",
                  thumbnail_url: d.thumbnail_url ?? d.cover_image_url ?? null, views: d.views ?? d.view_count ?? 0,
                });
              }
            }
          } catch (err) {
            logger.error("Settings/Liked: fetch detail failed", { contentType, contentId, err });
          }
        }
        if (!cancelled) setLikedItems(combined);
      } catch (err) {
        logger.error("Settings/Liked: getLikes failed", err);
        if (!cancelled) setLikedItems([]);
      } finally {
        if (!cancelled) setLikedLoading(false);
      }
    };
    fetchLiked();
    return () => { cancelled = true; };
  }, [activeTab, apiClient]);

  const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };
  const CARD = {
    background: "#FFFFFF",
    border: "2px solid rgba(11,26,51,0.06)",
    borderRadius: 24,
    boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.06)",
  };
  const PRIMARY_PILL = {
    ...FREDOKA,
    background: "#0065F8",
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: 14,
    border: "none",
    borderRadius: 999,
    padding: "12px 22px",
    boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
    cursor: "pointer",
  };
  const SECONDARY_PILL = {
    ...FREDOKA,
    background: "#FFFFFF",
    color: "#0065F8",
    fontWeight: 700,
    fontSize: 14,
    border: "2px solid rgba(0,101,248,0.22)",
    borderRadius: 999,
    padding: "10px 20px",
    boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
    cursor: "pointer",
  };
  const INPUT_BASE = {
    ...FREDOKA,
    width: "100%",
    background: "#FFFFFF",
    color: "#0B1A33",
    fontSize: 15,
    fontWeight: 500,
    border: "2px solid rgba(11,26,51,0.10)",
    borderRadius: 14,
    padding: "12px 16px",
    outline: "none",
    transition: "border-color 200ms",
  };
  const EYEBROW = { ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" };
  const TITLE = { ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: "clamp(1.75rem, 3.4vw, 2.4rem)", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1.05, margin: "6px 0 4px" };
  const SUBTITLE = { ...FREDOKA, color: "#5A6B85", fontSize: 15, margin: 0 };

  const TabIcon = ({ id, active }) => {
    const common = { width: 18, height: 18, strokeWidth: 2.2 };
    switch (id) {
      case "profile":
        return (<svg {...common} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16 14a4 4 0 10-8 0M20 21v-1a6 6 0 00-6-6h-4a6 6 0 00-6 6v1M12 11a4 4 0 100-8 4 4 0 000 8z"/></svg>);
      case "stats":
        return (<svg {...common} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l3-3 3 3 5-5 4 4 3-3M3 19h18"/></svg>);
      case "shop":
        return (<img src="/assets/pixley_stars.png" alt="" style={{ width: 18, height: 18, filter: active ? "brightness(0) invert(1)" : "none" }} />);
      case "referral":
        return (<svg {...common} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 11a4 4 0 100-8 4 4 0 000 8zM7 11a4 4 0 100-8 4 4 0 000 8zM2 21v-1a5 5 0 015-5h2M14 21v-1a5 5 0 015-5h2"/></svg>);
      case "gift":
        return (<svg {...common} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20 12v8H4v-8M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 110-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 100-5C13 2 12 7 12 7z"/></svg>);
      case "liked":
        return (<svg {...common} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>);
      case "interests":
        return (<svg {...common} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 2l2.39 4.84L20 7.27l-4 3.91.94 5.5L12 14.77l-4.94 2.92.94-5.5-4-3.91 5.61-.43L12 2z"/></svg>);
      case "shows":
        return (<svg {...common} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2 7h20v12H2zM7 22h10M12 19v3"/></svg>);
      case "account":
        return (<svg {...common} fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round"/><path strokeLinecap="round" strokeLinejoin="round" d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 008 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H2a2 2 0 110-4h.09A1.65 1.65 0 004.6 8a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V2a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H22a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>);
      default:
        return null;
    }
  };

  const tabs = [
    { id: "profile", label: "Profile" },
    { id: "stats", label: "Subscription" },
    { id: "shop", label: "Star Shop" },
    { id: "referral", label: "Referrals" },
    { id: "gift", label: "Send a Gift" },
    { id: "liked", label: "Liked" },
    { id: "interests", label: "Interests" },
    { id: "shows", label: "Favorite Shows" },
    { id: "account", label: "Account" },
  ];

  return (
    <div
      className="min-h-screen relative"
      style={{
        background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
        paddingBottom: 1,
      }}
    >
      {/* Soft brand blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div style={{ position: "absolute", top: -200, left: -180, width: 520, height: 520, borderRadius: "50%", background: "rgba(0,101,248,0.08)", filter: "blur(30px)" }} />
        <div style={{ position: "absolute", bottom: -240, right: -220, width: 560, height: 560, borderRadius: "50%", background: "rgba(0,202,255,0.08)", filter: "blur(30px)" }} />
        <div style={{ position: "absolute", top: "40%", left: "55%", width: 320, height: 320, borderRadius: "50%", background: "rgba(2,132,199,0.06)", filter: "blur(30px)" }} />
      </div>

      {/* Status banners (floating, top of content) */}
      {(success || error) && (
        <div className="relative z-20 pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {success && (
            <div
              className="px-5 py-3"
              style={{
                ...FREDOKA,
                background: "#FFFFFF",
                color: "#0369A1",
                fontWeight: 700,
                fontSize: 14,
                border: "2px solid rgba(2,132,199,0.35)",
                borderRadius: 14,
                boxShadow: "0px 3px 0px rgba(11,26,51,0.05)",
              }}
            >
              {success}
            </div>
          )}
          {error && (
            <div
              className="px-5 py-3 mt-2"
              style={{
                ...FREDOKA,
                background: "#FFFFFF",
                color: "#B91C1C",
                fontWeight: 700,
                fontSize: 14,
                border: "2px solid rgba(220,38,38,0.30)",
                borderRadius: 14,
                boxShadow: "0px 3px 0px rgba(11,26,51,0.05)",
              }}
            >
              {error}
            </div>
          )}
        </div>
      )}

      {/* Main Layout */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <motion.aside
            className="w-full lg:w-72 flex-shrink-0"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div
              className="lg:sticky lg:top-24"
              style={{ ...CARD, padding: "18px 14px", overflow: "hidden" }}
            >
              {/* Sidebar Header */}
              <div className="px-3 pt-2 pb-3 hidden lg:flex items-center gap-3">
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
                    boxShadow: "0px 3px 0px #073B8E",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg width="20" height="20" fill="none" stroke="#FFFFFF" strokeWidth="2.4" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h16M4 12h16M4 19h7" />
                  </svg>
                </div>
                <div>
                  <p style={{ ...EYEBROW, margin: 0 }}>Settings</p>
                  <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 12, margin: "2px 0 0" }}>Manage your account</p>
                </div>
              </div>

              <nav className="flex lg:flex-col gap-1 px-1 pt-1 lg:overflow-visible overflow-x-auto lg:pb-1" style={{ scrollbarWidth: "none" }}>
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className="flex-shrink-0 lg:w-full flex items-center gap-2.5 transition-all active:scale-[0.98]"
                      style={{
                        ...FREDOKA,
                        background: isActive ? "#0065F8" : "transparent",
                        color: isActive ? "#FFFFFF" : "#5A6B85",
                        fontWeight: 700,
                        fontSize: 13,
                        border: "none",
                        borderRadius: 999,
                        padding: "10px 16px",
                        whiteSpace: "nowrap",
                        boxShadow: isActive ? "0px 3px 0px #073B8E" : "none",
                        cursor: "pointer",
                      }}
                    >
                      <TabIcon id={tab.id} active={isActive} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>

              {/* Sidebar Footer */}
              <div className="hidden lg:block mt-4 pt-3 px-1" style={{ borderTop: "2px solid rgba(11,26,51,0.06)" }}>
                <button
                  onClick={signOut}
                  className="w-full flex items-center gap-2.5 transition active:scale-[0.98]"
                  style={{
                    ...FREDOKA,
                    background: "transparent",
                    color: "#94A3B8",
                    fontWeight: 600,
                    fontSize: 12,
                    border: "none",
                    borderRadius: 999,
                    padding: "10px 16px",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "#DC2626"; e.currentTarget.style.background = "rgba(220,38,38,0.06)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = "#94A3B8"; e.currentTarget.style.background = "transparent"; }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
                  </svg>
                  Sign out
                </button>
              </div>
            </div>
          </motion.aside>

          {/* Content Area */}
          <div className="flex-1 min-w-0 pb-6">
            <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
            {/* Profile Tab */}
            {activeTab === "profile" && (
              <div>
                <div className="mb-8">
                  <span style={EYEBROW}>Profile</span>
                  <h2 style={TITLE}>Your Storyteller Card</h2>
                  <p style={SUBTITLE}>Customize how you show up across Pixley.</p>
                </div>

                {/* Profile Picture Confirmation Modal */}
                {showPfpConfirm && pfpPreview && (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{ background: "rgba(11,26,51,0.42)", backdropFilter: "blur(8px)" }}
                    onClick={handlePfpCancelUpload}
                  >
                    <div
                      className="w-full max-w-sm text-center"
                      style={{ ...CARD, padding: "28px", boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span style={EYEBROW}>New Photo</span>
                      <h3 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 22, textTransform: "uppercase", margin: "6px 0 6px" }}>Looking good?</h3>
                      <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: "0 0 18px" }}>This becomes your profile picture.</p>
                      <div
                        className="mx-auto mb-5 overflow-hidden"
                        style={{ width: 176, height: 176, borderRadius: "50%", border: "4px solid #FFFFFF", boxShadow: "0px 4px 0px rgba(11,26,51,0.08), 0px 0px 0px 4px rgba(0,101,248,0.15)" }}
                      >
                        <img src={pfpPreview} alt="Preview" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex gap-3">
                        <button onClick={handlePfpCancelUpload} className="flex-1 py-3" style={{ ...SECONDARY_PILL, color: "#5A6B85", borderColor: "rgba(11,26,51,0.10)", padding: "12px 16px" }}>
                          Cancel
                        </button>
                        <button onClick={handlePfpConfirmUpload} className="flex-1" style={{ ...PRIMARY_PILL, padding: "12px 16px" }}>
                          Use this photo
                        </button>
                      </div>
                      <button
                        onClick={() => { handlePfpCancelUpload(); setTimeout(() => pfpInputRef.current?.click(), 100); }}
                        className="mt-4"
                        style={{ ...FREDOKA, background: "transparent", border: "none", color: "#0065F8", fontWeight: 700, fontSize: 12, cursor: "pointer", textDecoration: "underline" }}
                      >
                        Choose a different photo
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-12 gap-6">
                  {/* Profile Picture Card */}
                  <div className="col-span-12 lg:col-span-4 flex flex-col items-center text-center relative overflow-hidden" style={{ ...CARD, padding: "32px 24px" }}>
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: "linear-gradient(90deg, #0065F8 0%, #00CAFF 100%)" }} />
                    <input
                      ref={pfpInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleProfilePictureSelect}
                      disabled={uploadingPfp}
                    />
                    <div
                      className="relative overflow-hidden cursor-pointer group/avatar"
                      onClick={() => !uploadingPfp && pfpInputRef.current?.click()}
                      style={{
                        width: 160,
                        height: 160,
                        borderRadius: "50%",
                        border: "4px solid #FFFFFF",
                        boxShadow: "0px 4px 0px rgba(11,26,51,0.08), 0px 0px 0px 4px rgba(0,101,248,0.18)",
                        marginBottom: 18,
                      }}
                    >
                      {pfpPreview ? (
                        <img src={pfpPreview} alt="Preview" className="w-full h-full object-cover" />
                      ) : user?.profile_picture && !user.profile_picture.startsWith("default:") ? (
                        <img src={user.profile_picture} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)" }}>
                          <span style={{ ...FREDOKA, color: "#FFFFFF", fontWeight: 700, fontSize: 52 }}>
                            {user?.profile_picture?.startsWith("default:") ? user.profile_picture.split(":")[1] : user?.name?.charAt(0) || user?.email?.charAt(0) || "?"}
                          </span>
                        </div>
                      )}
                      {uploadingPfp ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ background: "rgba(11,26,51,0.6)" }}>
                          <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid rgba(255,255,255,0.30)", borderTopColor: "#FFFFFF", animation: "settings-spin 1s linear infinite", marginBottom: 8 }} />
                          <span style={{ ...FREDOKA, color: "#FFFFFF", fontSize: 11, fontWeight: 700 }}>{pfpStatus}</span>
                        </div>
                      ) : pfpStatus === "Updated!" ? (
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,101,248,0.78)" }}>
                          <svg width="40" height="40" fill="none" stroke="#FFFFFF" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : (
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity"
                          style={{ background: "rgba(11,26,51,0.55)" }}
                        >
                          <svg width="28" height="28" fill="none" stroke="#FFFFFF" strokeWidth="2.2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2zM12 17a4 4 0 100-8 4 4 0 000 8z" />
                          </svg>
                          <span style={{ ...FREDOKA, color: "#FFFFFF", fontSize: 11, fontWeight: 700, marginTop: 4 }}>Change Photo</span>
                        </div>
                      )}
                    </div>
                    <h3 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 20, margin: 0 }}>
                      {user?.name || user?.username || "Pixley Creator"}
                    </h3>
                    <p style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", margin: "6px 0 0" }}>
                      Storyteller
                    </p>

                    <div className="grid grid-cols-2 gap-3 w-full mt-6">
                      <div className="text-center" style={{ background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)", border: "2px solid rgba(0,101,248,0.18)", borderRadius: 16, padding: "14px 10px" }}>
                        <div style={{ ...FREDOKA, color: "#0065F8", fontWeight: 800, fontSize: 26, lineHeight: 1 }}>{stats.storiesCount || 0}</div>
                        <div style={{ ...FREDOKA, color: "#5A6B85", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginTop: 4 }}>Stories</div>
                      </div>
                      <div className="text-center" style={{ background: "linear-gradient(180deg, #E0F2FE 0%, #BAE6FD 100%)", border: "2px solid rgba(2,132,199,0.25)", borderRadius: 16, padding: "14px 10px" }}>
                        <div className="flex items-center justify-center gap-1.5" style={{ ...FREDOKA, color: "#0369A1", fontWeight: 800, fontSize: 26, lineHeight: 1 }}>
                          {user?.pixley_stars ?? 0}
                          <img src="/assets/pixley_stars.png" alt="" style={{ width: 18, height: 18 }} />
                        </div>
                        <div style={{ ...FREDOKA, color: "#0C4A6E", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginTop: 4 }}>Stars</div>
                      </div>
                    </div>
                  </div>

                  {/* Account Information */}
                  <div className="col-span-12 lg:col-span-8 flex flex-col" style={{ ...CARD, padding: "28px" }}>
                    <div className="flex items-center gap-2.5 mb-6">
                      <span style={EYEBROW}>Account Info</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Full Name</label>
                        <input
                          type="text"
                          value={user?.name || "Not provided"}
                          disabled
                          style={{ ...INPUT_BASE, background: "rgba(11,26,51,0.04)", color: "#5A6B85", cursor: "not-allowed" }}
                        />
                      </div>
                      <div>
                        <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Username</label>
                        <input
                          type="text"
                          value={profileForm.username}
                          onChange={(e) => setProfileForm((prev) => ({ ...prev, username: e.target.value }))}
                          style={INPUT_BASE}
                          onFocus={(e) => (e.currentTarget.style.borderColor = "#0065F8")}
                          onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(11,26,51,0.10)")}
                          placeholder="Enter your username"
                          disabled={isLoading}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Email Address</label>
                        <input
                          type="email"
                          value={user?.email || "Not provided"}
                          disabled
                          style={{ ...INPUT_BASE, background: "rgba(11,26,51,0.04)", color: "#5A6B85", cursor: "not-allowed" }}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end mt-auto pt-6">
                      <button
                        type="button"
                        onClick={handleProfileSubmit}
                        disabled={isLoading || profileForm.username === (user?.username || user?.name)}
                        className="transition active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed"
                        style={PRIMARY_PILL}
                      >
                        {isLoading ? "Saving…" : "Save changes"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Liked Shows Tab */}
            {activeTab === "liked" && (
              <div>
                <div className="mb-8">
                  <span style={EYEBROW}>Your Favorites</span>
                  <h2 style={TITLE}>Hearted by you</h2>
                  <p style={SUBTITLE}>
                    {likedItems.length > 0
                      ? `${likedItems.length} storybook${likedItems.length !== 1 ? "s" : ""} and video${likedItems.length !== 1 ? "s" : ""} you've saved.`
                      : "Storybooks and videos you've loved will live here."}
                  </p>
                </div>

                {likedLoading ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="aspect-[3/4] animate-pulse"
                        style={{ background: "rgba(11,26,51,0.06)", borderRadius: 20 }}
                      />
                    ))}
                  </div>
                ) : likedItems.length === 0 ? (
                  <div className="text-center mx-auto" style={{ ...CARD, padding: "56px 32px", maxWidth: 520 }}>
                    <div
                      className="mx-auto mb-5 flex items-center justify-center"
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, rgba(2,132,199,0.18) 0%, rgba(2,132,199,0.06) 100%)",
                      }}
                    >
                      <svg width="40" height="40" fill="#0369A1" viewBox="0 0 24 24">
                        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
                      </svg>
                    </div>
                    <h3 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 20, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "0 0 6px" }}>
                      No favorites yet
                    </h3>
                    <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: "0 auto 18px", maxWidth: 320 }}>
                      Tap the heart on any storybook or video and it'll show up here.
                    </p>
                    <button onClick={() => navigate("/storyverse")} style={PRIMARY_PILL} className="transition active:scale-[0.97]">
                      Explore Storyverse
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                    {likedItems.map((item) => (
                      <LikedContentCard
                        key={`${item.type}-${item.id}`}
                        id={item.id}
                        type={item.type}
                        title={item.title}
                        description={item.description}
                        thumbnail={item.thumbnail_url}
                        views={item.views}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Stats/Subscription Tab */}
            {activeTab === "stats" && (
              <div>
                <div className="mb-8">
                  <span style={EYEBROW}>Subscription</span>
                  <h2 style={TITLE}>Your Pixley Plan</h2>
                  <p style={SUBTITLE}>Track your creative power and weekly star deliveries.</p>
                </div>

                {statsLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid rgba(0,101,248,0.20)", borderTopColor: "#0065F8", animation: "settings-spin 1s linear infinite" }} />
                    <span style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, marginLeft: 12, fontWeight: 600 }}>Loading stats…</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-12 gap-6">
                    {/* Plan Hero */}
                    {subscriptionStatus.subscriptionType === "NONE" ? (
                      <div
                        className="col-span-12 lg:col-span-8 relative overflow-hidden flex flex-col justify-between"
                        style={{
                          background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
                          borderRadius: 28,
                          padding: "32px",
                          minHeight: 340,
                          color: "#FFFFFF",
                          boxShadow: "0px 6px 0px rgba(7,59,142,0.40), 0px 24px 48px rgba(0,101,248,0.30)",
                        }}
                      >
                        <div style={{ position: "absolute", top: -60, right: -60, width: 240, height: 240, borderRadius: "50%", background: "rgba(255,255,255,0.12)", filter: "blur(20px)" }} />
                        <div className="relative z-10">
                          <span
                            className="inline-block"
                            style={{
                              ...FREDOKA,
                              background: "rgba(255,255,255,0.20)",
                              color: "#FFFFFF",
                              fontSize: 11,
                              fontWeight: 800,
                              letterSpacing: "0.18em",
                              textTransform: "uppercase",
                              padding: "6px 14px",
                              borderRadius: 999,
                              border: "1.5px solid rgba(255,255,255,0.30)",
                            }}
                          >
                            Current Plan
                          </span>
                          <h2 style={{ ...FREDOKA, fontWeight: 700, fontSize: "clamp(2.4rem, 4vw, 3.4rem)", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, margin: "16px 0 8px" }}>
                            {cutoverLive ? "No Membership" : "Free Plan"}
                          </h2>
                          <p style={{ ...FREDOKA, color: "rgba(255,255,255,0.85)", fontSize: 15, margin: "0 0 22px" }}>
                            {cutoverLive
                              ? "Start your membership to create stories, characters, and storybooks."
                              : "Unlock longer stories, more stars, and family-friendly perks."}
                          </p>
                          <div className="grid grid-cols-3 gap-2.5">
                            {[
                              { label: "Weekly Stars", value: "Basic" },
                              { label: "Story Length", value: "Standard" },
                              { label: "Languages", value: "Limited" },
                            ].map((c) => (
                              <div key={c.label} style={{ background: "rgba(255,255,255,0.14)", border: "1.5px solid rgba(255,255,255,0.22)", borderRadius: 14, padding: "10px 12px" }}>
                                <div style={{ ...FREDOKA, color: "rgba(255,255,255,0.75)", fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>{c.label}</div>
                                <div style={{ ...FREDOKA, color: "#FFFFFF", fontWeight: 700, fontSize: 14, marginTop: 2 }}>{c.value}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="relative z-10 mt-6">
                          <button
                            onClick={handleUpgrade}
                            className="inline-flex items-center gap-2 transition active:scale-[0.97]"
                            style={{
                              ...FREDOKA,
                              background: "#FFFFFF",
                              color: "#0065F8",
                              fontWeight: 800,
                              fontSize: 14,
                              border: "none",
                              borderRadius: 999,
                              padding: "12px 22px",
                              boxShadow: "0px 4px 0px rgba(7,59,142,0.30)",
                              cursor: "pointer",
                            }}
                          >
                            View all plans
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="col-span-12 lg:col-span-8 relative overflow-hidden flex flex-col justify-between" style={{ ...CARD, padding: "32px", minHeight: 340 }}>
                        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 6, background: "linear-gradient(90deg, #0065F8 0%, #00CAFF 100%)" }} />
                        <div className="relative z-10">
                          <span
                            className="inline-flex items-center gap-1.5"
                            style={{
                              ...FREDOKA,
                              background: "rgba(0,101,248,0.10)",
                              color: "#0065F8",
                              fontSize: 11,
                              fontWeight: 800,
                              letterSpacing: "0.18em",
                              textTransform: "uppercase",
                              padding: "6px 14px",
                              borderRadius: 999,
                            }}
                          >
                            <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                            Active Plan
                          </span>
                          <h2 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: "clamp(2.2rem, 4vw, 3.2rem)", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, margin: "14px 0 8px" }}>
                            {subscriptionStatus.subscriptionType === "EDDY" ? "EDDY Plan" : (subscriptionStatus.subscriptionType?.charAt(0) + subscriptionStatus.subscriptionType?.slice(1).toLowerCase()) || "Premium"}
                          </h2>
                          <div className="flex items-center gap-2" style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, marginBottom: 18 }}>
                            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: subscriptionStatus.cancel_at_period_end ? "#94A3B8" : "#0065F8" }} />
                            {subscriptionStatus.cancel_at_period_end
                              ? (() => {
                                  const end = subscriptionStatus.current_period_end;
                                  const date = typeof end === "number" ? new Date(end * (end < 1e12 ? 1000 : 1)) : new Date(end);
                                  const formatted = Number.isNaN(date.getTime()) ? null : date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
                                  return formatted ? `Ends ${formatted}` : "Scheduled to end";
                                })()
                              : "Active subscription"}
                          </div>
                          {subscriptionStatus.managementPlatform === "app_store" && (
                            <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: "0 0 16px" }}>
                              In-app purchase subscription. Manage through the App Store.
                            </p>
                          )}
                          {subscriptionStatus.managementPlatform === "rc_billing" && (
                            <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: "0 0 16px" }}>
                              Billed through our web checkout. Change your plan, update your card, or cancel from your billing page.
                            </p>
                          )}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                            {[
                              { label: "Weekly Stars", value: "Premium" },
                              { label: "Story Length", value: "Extended" },
                              { label: "Languages", value: "All" },
                              { label: "Downloads", value: "Enabled" },
                            ].map((c) => (
                              <div key={c.label} style={{ background: "rgba(0,101,248,0.06)", border: "1.5px solid rgba(0,101,248,0.15)", borderRadius: 14, padding: "10px 12px" }}>
                                <div style={{ ...FREDOKA, color: "#5A6B85", fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>{c.label}</div>
                                <div style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 14, marginTop: 2 }}>{c.value}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="relative z-10 mt-6">
                          {(subscriptionStatus.subscriptionType === "STRIPE" || subscriptionStatus.subscriptionType === "EDDY" || subscriptionStatus.subscriptionType === "PREMIUM" || subscriptionStatus.subscriptionType === "STARTER" || subscriptionStatus.subscriptionType === "PLUS" || subscriptionStatus.subscriptionType === "PRO" || subscriptionStatus.subscriptionType === "FAMILY" || subscriptionStatus.subscriptionType === "UNLIMITED") && (
                            <div className="flex flex-wrap items-center gap-3">
                              <StartMembershipNow variant="pill" />
                              {(subscriptionStatus.managementPlatform === "stripe" || subscriptionStatus.managementPlatform === "rc_billing") && (
                                <button onClick={handleManageSubscription} className="transition active:scale-[0.97]" style={PRIMARY_PILL}>
                                  Manage subscription
                                </button>
                              )}
                              {subscriptionStatus.managementPlatform === "app_store" && (
                                <a
                                  href="https://apps.apple.com/account/subscriptions"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="transition active:scale-[0.97]"
                                  style={{ ...PRIMARY_PILL, textDecoration: "none", display: "inline-flex", alignItems: "center" }}
                                >
                                  Manage in App Store
                                </a>
                              )}
                              <button onClick={() => setShowSubscriptionModal(true)} className="transition active:scale-[0.97]" style={SECONDARY_PILL}>
                                View all plans
                              </button>
                              {(subscriptionStatus.managementPlatform === "stripe" || subscriptionStatus.managementPlatform === "rc_billing") && !subscriptionStatus.cancel_at_period_end && (
                                <button
                                  onClick={() => setShowCancelSubscriptionModal(true)}
                                  style={{ ...FREDOKA, background: "transparent", border: "none", color: "#94A3B8", fontWeight: 600, fontSize: 13, cursor: "pointer", textDecoration: "underline", marginLeft: 4 }}
                                >
                                  Cancel subscription
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Next Star Delivery */}
                    {(() => {
                      // Stars refresh weekly: the backend tops a user up once
                      // pixley_stars_last_refreshed is >= 7 days old (stars_refresh_job).
                      // So the next delivery is last-refresh + 7 days.
                      const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
                      const now = nowTs;
                      const lastRaw = user?.pixley_stars_last_refreshed;
                      const lastMs = lastRaw ? new Date(lastRaw).getTime() : NaN;
                      const hasLast = !Number.isNaN(lastMs);
                      // No known last-refresh => a refill is already due (job grants
                      // on its next run), so the countdown is effectively zero.
                      const msLeft = hasLast ? Math.max(0, lastMs + WEEK_MS - now) : 0;
                      const daysUntil = Math.floor(msLeft / (1000 * 60 * 60 * 24));
                      const hoursLeft = Math.floor((msLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                      const minsLeft = Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60));
                      // Bar shows how far through the 7-day cycle we are.
                      const cycleProgress = hasLast
                        ? Math.min(100, Math.max(0, Math.round(((now - lastMs) / WEEK_MS) * 100)))
                        : 100;
                      const starAmount = costs?.star_delivery ?? starDeliveryAmount ?? 50;

                      return (
                        <div
                          className="col-span-12 lg:col-span-4 relative overflow-hidden flex flex-col justify-between"
                          style={{
                            background: "linear-gradient(180deg, #CFFAFE 0%, #A5F3FC 100%)",
                            border: "2px solid rgba(6,182,212,0.28)",
                            borderRadius: 28,
                            padding: "28px",
                            boxShadow: "0px 4px 0px rgba(6,182,212,0.20), 0px 14px 28px rgba(6,182,212,0.14)",
                          }}
                        >
                          {/* shooting-star decoration */}
                          <div style={{ position: "absolute", top: -10, right: -10, width: 150, height: 150, opacity: 0.22 }}>
                            <svg viewBox="0 0 100 100" width="100%" height="100%">
                              <path d="M15 85L60 35M55 30l8 8" stroke="#155E75" strokeWidth="3" strokeLinecap="round" fill="none" />
                              <path d="M75 25l3 6 6 3-6 3-3 6-3-6-6-3 6-3z" fill="#155E75" />
                            </svg>
                          </div>
                          <div className="relative z-10">
                            <span style={{ ...FREDOKA, color: "#0E7490", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                              Next Delivery
                            </span>
                            <h3 style={{ ...FREDOKA, color: "#155E75", fontWeight: 700, fontSize: 22, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "6px 0 4px" }}>
                              Fresh stars
                            </h3>
                            <p style={{ ...FREDOKA, color: "rgba(21,94,117,0.78)", fontSize: 13, margin: "0 0 18px" }}>
                              Your weekly refill arrives in:
                            </p>
                            <div className="grid grid-cols-3 gap-2">
                              {[
                                { v: daysUntil, l: "Days" },
                                { v: hoursLeft, l: "Hrs" },
                                { v: minsLeft, l: "Min" },
                              ].map((c) => (
                                <div key={c.l} className="text-center" style={{ background: "rgba(255,255,255,0.80)", border: "2px solid rgba(6,182,212,0.24)", borderRadius: 14, padding: "10px 4px" }}>
                                  <div style={{ ...FREDOKA, color: "#155E75", fontWeight: 800, fontSize: 24, lineHeight: 1 }}>{String(c.v).padStart(2, "0")}</div>
                                  <div style={{ ...FREDOKA, color: "rgba(21,94,117,0.65)", fontSize: 9, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginTop: 4 }}>{c.l}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="relative z-10 mt-5">
                            <div className="flex justify-between" style={{ ...FREDOKA, color: "#155E75", fontWeight: 700, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 6 }}>
                              <span>Allowance</span>
                              <span className="inline-flex items-center gap-1">
                                {starAmount}
                                <img src="/assets/pixley_stars.png" alt="" style={{ width: 12, height: 12 }} />
                              </span>
                            </div>
                            <div style={{ height: 10, background: "rgba(21,94,117,0.14)", borderRadius: 999, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${cycleProgress}%`, background: "linear-gradient(90deg, #0891B2 0%, #67E8F9 100%)", borderRadius: 999 }} />
                            </div>
                            <button
                              onClick={() => setActiveTab("shop")}
                              className="w-full mt-4 transition active:scale-[0.97]"
                              style={{
                                ...FREDOKA,
                                background: "#0E7490",
                                color: "#FFFFFF",
                                fontWeight: 700,
                                fontSize: 14,
                                border: "none",
                                borderRadius: 999,
                                padding: "12px 18px",
                                boxShadow: "0px 3px 0px #155E75",
                                cursor: "pointer",
                              }}
                            >
                              Buy more stars
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Shop Tab */}
            {activeTab === "shop" && (
              <div>
                {/* Header */}
                <div className="mb-6">
                  <span style={EYEBROW}>Star Shop</span>
                  <h2 style={TITLE}>Power your imagination</h2>
                  <p style={SUBTITLE}>Pixley Stars unlock new characters, stories, and storybook generations.</p>
                </div>

                {/* Balance Card */}
                <section
                  className="relative mb-8 overflow-hidden flex flex-col sm:flex-row items-center justify-between gap-5"
                  style={{
                    background: "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)",
                    border: "2px solid rgba(2,132,199,0.25)",
                    borderRadius: 24,
                    padding: "20px 24px",
                    boxShadow: "0px 4px 0px rgba(2,132,199,0.18), 0px 14px 28px rgba(2,132,199,0.12)",
                  }}
                >
                  {/* Constellation decoration */}
                  <div style={{ position: "absolute", top: 8, right: 200, width: 200, height: 100, opacity: 0.30, pointerEvents: "none" }}>
                    <svg viewBox="0 0 200 100" width="100%" height="100%">
                      <line x1="20" y1="60" x2="60" y2="30" stroke="#0369A1" strokeWidth="1.5" strokeDasharray="3,3" />
                      <line x1="60" y1="30" x2="110" y2="55" stroke="#0369A1" strokeWidth="1.5" strokeDasharray="3,3" />
                      <line x1="110" y1="55" x2="160" y2="25" stroke="#0369A1" strokeWidth="1.5" strokeDasharray="3,3" />
                      <circle cx="20" cy="60" r="3" fill="#0369A1" />
                      <circle cx="60" cy="30" r="3" fill="#0369A1" />
                      <circle cx="110" cy="55" r="3" fill="#0369A1" />
                      <circle cx="160" cy="25" r="3" fill="#0369A1" />
                    </svg>
                  </div>
                  <div className="relative z-10 max-w-lg">
                    <span style={{ ...FREDOKA, color: "#0369A1", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>Your Balance</span>
                    <h3 style={{ ...FREDOKA, color: "#0C4A6E", fontWeight: 700, fontSize: 22, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 4px" }}>
                      Pixley Stars
                    </h3>
                    <p style={{ ...FREDOKA, color: "#075985", fontSize: 13, margin: 0 }}>
                      {cutoverLive && !hasEntitlement
                        ? "Your stars are saved and waiting — they unlock the moment your membership starts."
                        : "Spend them on characters, stories, and storybooks — they refresh weekly with your plan."}
                    </p>
                    {cutoverLive && !hasEntitlement && (
                      <button
                        onClick={() => openWall({ trigger: "stars" })}
                        style={{ ...FREDOKA, marginTop: 10, padding: "10px 22px", borderRadius: 12, border: "none", background: "#0065F8", color: "#FFF", fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0px 3px 0px #073B8E" }}
                      >
                        Start your membership
                      </button>
                    )}
                  </div>
                  <div className="relative z-10 flex-shrink-0">
                    <div
                      className="flex flex-col items-center"
                      style={{
                        background: "#FFFFFF",
                        border: "2px solid rgba(2,132,199,0.30)",
                        borderRadius: 20,
                        padding: "16px 22px",
                        minWidth: 150,
                        boxShadow: "0px 3px 0px rgba(2,132,199,0.22)",
                      }}
                    >
                      <img src="/assets/pixley_stars.png" alt="" style={{ width: 36, height: 36, marginBottom: 6 }} />
                      <div style={{ ...FREDOKA, color: "#0369A1", fontWeight: 800, fontSize: 32, lineHeight: 1 }}>{user?.pixley_stars ?? 0}</div>
                      <div style={{ ...FREDOKA, color: "#0C4A6E", fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", marginTop: 4 }}>Current Stars</div>
                    </div>
                  </div>
                </section>

                {/* Packs */}
                <section>
                  <div className="mb-5">
                    <span style={EYEBROW}>Choose a Pack</span>
                    <h3 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 22, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 0" }}>Top up your stars</h3>
                  </div>

                  {shopLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid rgba(0,101,248,0.20)", borderTopColor: "#0065F8", animation: "settings-spin 1s linear infinite" }} />
                      <span style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, marginLeft: 12, fontWeight: 600 }}>Loading packs…</span>
                    </div>
                  ) : cutoverLive && !hasEntitlement ? (
                    <div className="flex items-center justify-center py-16">
                      <span style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14 }}>Star packs are available to members.</span>
                    </div>
                  ) : shopError ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <div style={{ ...FREDOKA, color: "#B91C1C", fontSize: 14, marginBottom: 14 }}>{shopError}</div>
                      <button onClick={loadStarShopProducts} style={PRIMARY_PILL} className="transition active:scale-[0.97]">Retry</button>
                    </div>
                  ) : shopData.starAmounts.first_tier === null || shopData.starAmounts.second_tier === null || shopData.starAmounts.third_tier === null || shopData.starAmounts.fourth_tier === null ? (
                    <div className="flex items-center justify-center py-16">
                      <span style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14 }}>No packs available right now.</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                      {[
                        { id: "first_tier", name: "Starter Spark", price: "$4.99", count: shopData.starAmounts.first_tier, icon: "spark", popular: false },
                        { id: "second_tier", name: "Creative Flow", price: "$9.99", count: shopData.starAmounts.second_tier, icon: "stars", popular: true },
                        { id: "third_tier", name: "Story Weaver", price: "$19.99", count: shopData.starAmounts.third_tier, icon: "rocket", popular: false },
                        { id: "fourth_tier", name: "Legendary Pack", price: "$49.99", count: shopData.starAmounts.fourth_tier, icon: "diamond", popular: false },
                      ].map((pack) => {
                        const stripeId = shopData.stripeProductIds[pack.id];
                        return (
                          <div
                            key={pack.id}
                            className="relative flex flex-col"
                            style={{
                              background: "#FFFFFF",
                              border: pack.popular ? "2px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
                              borderRadius: 22,
                              padding: "22px",
                              boxShadow: pack.popular
                                ? "0px 5px 0px #073B8E, 0px 18px 36px rgba(0,101,248,0.18)"
                                : "0px 3px 0px rgba(11,26,51,0.06), 0px 10px 22px rgba(11,26,51,0.06)",
                            }}
                          >
                            {pack.popular && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: -12,
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  ...FREDOKA,
                                  background: "#0065F8",
                                  color: "#FFFFFF",
                                  fontWeight: 800,
                                  fontSize: 10,
                                  letterSpacing: "0.16em",
                                  textTransform: "uppercase",
                                  padding: "5px 12px",
                                  borderRadius: 999,
                                  boxShadow: "0px 2px 0px #073B8E",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Most Popular
                              </div>
                            )}
                            <div
                              className="mb-4 flex items-center justify-center"
                              style={{
                                width: 56,
                                height: 56,
                                borderRadius: 16,
                                background: pack.popular ? "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)" : "linear-gradient(135deg, #ECFEFF 0%, #CFFAFE 100%)",
                                boxShadow: pack.popular ? "0px 3px 0px #073B8E" : "0px 2px 0px rgba(8,145,178,0.18)",
                              }}
                            >
                              {pack.icon === "spark" && (
                                <svg width="26" height="26" fill="none" stroke={pack.popular ? "#FFFFFF" : "#0E7490"} strokeWidth="2.2" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4M4 19h4M13 3l2.5 6L21 11.5 15.5 14 13 20l-2.5-6L5 11.5 10.5 9 13 3z" />
                                </svg>
                              )}
                              {pack.icon === "stars" && (
                                <svg width="26" height="26" fill={pack.popular ? "#FFFFFF" : "#0E7490"} viewBox="0 0 24 24">
                                  <path d="M12 2l2.39 4.84L20 7.27l-4 3.91.94 5.5L12 14.77l-4.94 2.92.94-5.5-4-3.91 5.61-.43L12 2z" />
                                </svg>
                              )}
                              {pack.icon === "rocket" && (
                                <svg width="26" height="26" fill="none" stroke={pack.popular ? "#FFFFFF" : "#0E7490"} strokeWidth="2.2" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09zM12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2zM9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
                                </svg>
                              )}
                              {pack.icon === "diamond" && (
                                <svg width="26" height="26" fill="none" stroke={pack.popular ? "#FFFFFF" : "#0E7490"} strokeWidth="2.2" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 3h12l4 6-10 12L2 9l4-6zM12 21L6 9h12L12 21zM8 9l4-6 4 6" />
                                </svg>
                              )}
                            </div>
                            <div style={{ ...FREDOKA, color: pack.popular ? "#0065F8" : "#0E7490", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>
                              {pack.name}
                            </div>
                            <div className="flex items-baseline gap-1.5 mb-3">
                              <span style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 34, lineHeight: 1 }}>{pack.count}</span>
                              <span style={{ ...FREDOKA, color: "#5A6B85", fontWeight: 600, fontSize: 14 }}>stars</span>
                            </div>
                            <div className="mt-auto">
                              <div style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 18, marginBottom: 12 }}>{pack.price}</div>
                              <button
                                onClick={() => handleStarPurchase(pack.id)}
                                disabled={!stripeId}
                                className="w-full transition active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed"
                                style={pack.popular ? PRIMARY_PILL : { ...SECONDARY_PILL, color: "#0B1A33", borderColor: "rgba(11,26,51,0.12)", padding: "12px 16px", width: "100%" }}
                              >
                                {pack.popular ? "Get popular pack" : "Buy"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              </div>
            )}

            {/* Referral Tab */}
            {activeTab === "referral" && (
              <div>
                <div className="mb-8">
                  <span style={EYEBROW}>Referrals</span>
                  <h2 style={TITLE}>
                    Grow the <span style={{ color: "#0065F8" }}>Pixley family</span>
                  </h2>
                  <p style={SUBTITLE}>Invite friends — earn 125 stars per signup (max 3 per month).</p>
                </div>
                <div style={{ ...CARD, padding: 24 }}>
                  <ReferralSettingsSection />
                </div>
              </div>
            )}

            {/* Favorite Shows Tab */}
            {activeTab === "shows" && (
              <div>
                <header className="mb-8">
                  <span style={EYEBROW}>Personalization</span>
                  <h2 style={TITLE}>Favorite Shows</h2>
                  <p style={SUBTITLE}>The shows you pick shape the tone and style of your Pixley creations.</p>
                </header>

                {personalizationLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid rgba(0,101,248,0.20)", borderTopColor: "#0065F8", animation: "settings-spin 1s linear infinite" }} />
                    <span style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, marginLeft: 12, fontWeight: 600 }}>Loading shows…</span>
                  </div>
                ) : (
                  <>
                    {personalizationShows.length === 0 ? (
                      <div className="text-center" style={{ ...CARD, padding: "48px 32px" }}>
                        <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: 0 }}>No shows to personalize from right now.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                        {personalizationShows.map((show) => {
                          const isSelected = selectedShows.includes(show.id);
                          return (
                            <div
                              key={show.id}
                              onClick={() => toggleShowSelection(show.id)}
                              className="group relative aspect-[3/4] overflow-hidden cursor-pointer transition-all active:scale-[0.98]"
                              style={{
                                borderRadius: 20,
                                border: isSelected ? "3px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
                                boxShadow: isSelected
                                  ? "0px 5px 0px #073B8E, 0px 14px 28px rgba(0,101,248,0.20)"
                                  : "0px 3px 0px rgba(11,26,51,0.06), 0px 8px 18px rgba(11,26,51,0.06)",
                                background: "#FFFFFF",
                              }}
                            >
                              <img
                                src={show.image}
                                alt={show.title}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                onError={(e) => { e.target.onerror = null; e.target.src = "/assets/panda-logo.png"; }}
                              />
                              <div
                                className="absolute inset-0 flex flex-col justify-end p-4"
                                style={{ background: "linear-gradient(180deg, transparent 50%, rgba(11,26,51,0.85) 100%)" }}
                              >
                                <h3 style={{ ...FREDOKA, color: "#FFFFFF", fontWeight: 700, fontSize: 16, textTransform: "uppercase", letterSpacing: "-0.01em", margin: 0 }}>
                                  {show.title}
                                </h3>
                                {show.genre && (
                                  <p style={{ ...FREDOKA, color: "rgba(255,255,255,0.75)", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", margin: "4px 0 0" }}>
                                    {show.genre}
                                  </p>
                                )}
                              </div>
                              {isSelected && (
                                <div
                                  className="absolute"
                                  style={{
                                    top: 10,
                                    right: 10,
                                    width: 28,
                                    height: 28,
                                    background: "#0065F8",
                                    color: "#FFFFFF",
                                    borderRadius: "50%",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0px 2px 0px #073B8E",
                                  }}
                                >
                                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <p className="mt-6" style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, fontWeight: 600 }}>
                      <strong style={{ color: "#0B1A33" }}>{selectedShows.length}</strong> show{selectedShows.length !== 1 ? "s" : ""} selected · auto-saved
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Interests Tab */}
            {activeTab === "interests" && (
              <div>
                <header className="mb-6">
                  <span style={EYEBROW}>Personalization</span>
                  <h2 style={TITLE}>Your creative sparks</h2>
                  <p style={SUBTITLE}>
                    Pixley uses these to flavor characters, settings, and story beats just for you.
                  </p>
                </header>

                {personalizationLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid rgba(0,101,248,0.20)", borderTopColor: "#0065F8", animation: "settings-spin 1s linear infinite" }} />
                    <span style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, marginLeft: 12, fontWeight: 600 }}>Loading interests…</span>
                  </div>
                ) : (
                  <div>
                    {/* Search + Add Custom Input */}
                    <div className="mb-6">
                      <div className="relative max-w-xl">
                        <div
                          style={{
                            position: "absolute",
                            left: 8,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            background: "rgba(0,101,248,0.10)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pointerEvents: "none",
                          }}
                        >
                          <svg width="18" height="18" fill="none" stroke="#0065F8" strokeWidth="2.8" viewBox="0 0 24 24">
                            <circle cx="11" cy="11" r="7" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          value={customInterest}
                          onChange={(e) => setCustomInterest(e.target.value)}
                          onKeyPress={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomInterest(); } }}
                          style={{ ...INPUT_BASE, paddingRight: customInterest.trim() ? 110 : 16, borderRadius: 999, padding: "14px 16px 14px 56px" }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = "#0065F8"; e.currentTarget.style.boxShadow = "0 0 0 4px rgba(0,101,248,0.12)"; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(11,26,51,0.10)"; e.currentTarget.style.boxShadow = "none"; }}
                          placeholder="Search or add a custom interest"
                        />
                        {customInterest.trim() && (
                          <button
                            onClick={addCustomInterest}
                            style={{
                              ...FREDOKA,
                              position: "absolute",
                              right: 6,
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "#0065F8",
                              color: "#FFFFFF",
                              fontWeight: 700,
                              fontSize: 13,
                              border: "none",
                              borderRadius: 999,
                              padding: "8px 16px",
                              boxShadow: "0px 2px 0px #073B8E",
                              cursor: "pointer",
                            }}
                          >
                            + Add
                          </button>
                        )}
                      </div>
                      <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, fontWeight: 600, marginTop: 10 }}>
                        <strong style={{ color: "#0B1A33" }}>{selectedInterests.length}</strong> interest{selectedInterests.length !== 1 ? "s" : ""} selected · auto-saved
                      </p>
                    </div>

                    {/* Interest tiles */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {[...Object.keys(interestEmojis)]
                        .filter((i) => !customInterest.trim() || i.toLowerCase().includes(customInterest.toLowerCase()))
                        .sort((a, b) => {
                          const aSelected = selectedInterests.includes(a);
                          const bSelected = selectedInterests.includes(b);
                          if (aSelected && !bSelected) return -1;
                          if (!aSelected && bSelected) return 1;
                          return 0;
                        })
                        .map((interest) => {
                          const isSelected = selectedInterests.includes(interest);
                          return (
                            <div
                              key={interest}
                              onClick={() => toggleInterest(interest)}
                              className="relative cursor-pointer transition-all active:scale-[0.98]"
                              style={{
                                height: 140,
                                background: isSelected ? "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)" : "#FFFFFF",
                                border: isSelected ? "3px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
                                borderRadius: 18,
                                boxShadow: isSelected
                                  ? "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.20)"
                                  : "0px 3px 0px rgba(11,26,51,0.06)",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                className="absolute inset-0 flex items-center justify-center"
                                style={{
                                  fontSize: 72,
                                  opacity: isSelected ? 0.30 : 0.18,
                                  filter: isSelected ? "none" : "grayscale(40%)",
                                  transition: "all 200ms",
                                }}
                              >
                                {interestEmojis[interest]}
                              </div>
                              <div
                                className="absolute"
                                style={{
                                  left: 16,
                                  bottom: 14,
                                  ...FREDOKA,
                                  color: isSelected ? "#FFFFFF" : "#0B1A33",
                                  fontWeight: 700,
                                  fontSize: 18,
                                  textTransform: "uppercase",
                                  letterSpacing: "-0.01em",
                                  textShadow: isSelected ? "0px 1px 2px rgba(7,59,142,0.40)" : "none",
                                }}
                              >
                                {interest}
                              </div>
                              {isSelected && (
                                <div
                                  className="absolute"
                                  style={{
                                    top: 10,
                                    right: 10,
                                    width: 26,
                                    height: 26,
                                    background: "#FFFFFF",
                                    color: "#0065F8",
                                    borderRadius: "50%",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0px 2px 4px rgba(7,59,142,0.30)",
                                  }}
                                >
                                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            )}


            {/* Gift Tab */}
            {activeTab === "gift" && (
              <div>
                {/* Hero */}
                <div
                  className="mb-6 relative overflow-hidden flex flex-col md:flex-row items-center gap-6"
                  style={{
                    background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
                    borderRadius: 24,
                    padding: "28px 28px",
                    boxShadow: "0px 6px 0px rgba(7,59,142,0.35), 0px 18px 36px rgba(0,101,248,0.22)",
                    color: "#FFFFFF",
                  }}
                >
                  <div style={{ position: "absolute", top: -60, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.12)", filter: "blur(20px)" }} />
                  <div className="flex-1 z-10 relative">
                    <span style={{ ...FREDOKA, background: "rgba(255,255,255,0.20)", color: "#FFFFFF", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", padding: "5px 12px", borderRadius: 999, border: "1.5px solid rgba(255,255,255,0.30)", display: "inline-block" }}>
                      Gift the Magic
                    </span>
                    <h2 style={{ ...FREDOKA, color: "#FFFFFF", fontWeight: 700, fontSize: "clamp(1.6rem, 3vw, 2.2rem)", textTransform: "uppercase", letterSpacing: "-0.01em", lineHeight: 1.05, margin: "10px 0 6px" }}>
                      Share Pixley Premium
                    </h2>
                    <p style={{ ...FREDOKA, color: "rgba(255,255,255,0.92)", fontSize: 14, margin: 0, maxWidth: 440 }}>
                      Send a Pixley subscription to a friend or family member. They'll get their own login and a personal note from you.
                    </p>
                  </div>
                  <div
                    className="flex-shrink-0 flex items-center justify-center relative z-10"
                    style={{
                      width: 96,
                      height: 96,
                      borderRadius: 20,
                      background: "#FFFFFF",
                      border: "2px solid rgba(255,255,255,0.40)",
                      boxShadow: "0px 4px 0px rgba(7,59,142,0.30)",
                      transform: "rotate(4deg)",
                    }}
                  >
                    <svg width="48" height="48" fill="none" stroke="#0065F8" strokeWidth="2.2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M20 12v8H4v-8M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 110-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 100-5C13 2 12 7 12 7z" />
                    </svg>
                  </div>
                </div>

                <form onSubmit={handleGiftContinue}>
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Recipient Form */}
                    <section className="lg:col-span-7" style={{ ...CARD, padding: 24 }}>
                      <span style={EYEBROW}>Recipient</span>
                      <h3 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 20, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 18px" }}>
                        Who's it for?
                      </h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Recipient name</label>
                            <input
                              type="text"
                              value={giftRecipientName}
                              onChange={(e) => setGiftRecipientName(e.target.value)}
                              style={INPUT_BASE}
                              onFocus={(e) => (e.currentTarget.style.borderColor = "#0065F8")}
                              onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(11,26,51,0.10)")}
                              placeholder="Who is this gift for?"
                              required
                            />
                          </div>
                          <div>
                            <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Recipient email</label>
                            <input
                              type="email"
                              value={giftRecipientEmail}
                              onChange={(e) => setGiftRecipientEmail(e.target.value)}
                              style={INPUT_BASE}
                              onFocus={(e) => (e.currentTarget.style.borderColor = "#0065F8")}
                              onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(11,26,51,0.10)")}
                              placeholder="Where should we send it?"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Your name</label>
                          <input
                            type="text"
                            value={giftSenderName}
                            onChange={(e) => setGiftSenderName(e.target.value)}
                            style={INPUT_BASE}
                            onFocus={(e) => (e.currentTarget.style.borderColor = "#0065F8")}
                            onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(11,26,51,0.10)")}
                            placeholder="Let them know who it's from"
                          />
                        </div>
                        <div>
                          <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Personal message</label>
                          <textarea
                            value={giftPersonalMessage}
                            onChange={(e) => setGiftPersonalMessage(e.target.value)}
                            style={{ ...INPUT_BASE, resize: "none", padding: "14px 16px" }}
                            onFocus={(e) => (e.currentTarget.style.borderColor = "#0065F8")}
                            onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(11,26,51,0.10)")}
                            placeholder="Write a magical message…"
                            rows={4}
                          />
                        </div>
                      </div>
                    </section>

                    {/* Plan Selection */}
                    <section className="lg:col-span-5 flex flex-col" style={{ ...CARD, padding: 24 }}>
                      <span style={EYEBROW}>Choose Plan</span>
                      <h3 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 20, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 18px" }}>
                        Pick a length
                      </h3>
                      <div className="flex flex-col gap-3 flex-1">
                        {GIFT_PLANS.map((plan) => {
                          const isGiftSelected = giftSelectedPlanId === plan.id;
                          return (
                            <label key={plan.id} className="relative block cursor-pointer transition-all active:scale-[0.99]">
                              <input
                                type="radio"
                                name="gift_plan"
                                value={plan.id}
                                checked={isGiftSelected}
                                onChange={() => setGiftSelectedPlanId(plan.id)}
                                className="peer sr-only"
                              />
                              <div
                                className="relative overflow-hidden"
                                style={{
                                  padding: "16px 18px",
                                  background: isGiftSelected ? "rgba(0,101,248,0.06)" : "#FFFFFF",
                                  border: isGiftSelected ? "2px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
                                  borderRadius: 16,
                                  boxShadow: isGiftSelected
                                    ? "0px 3px 0px #073B8E, 0px 10px 22px rgba(0,101,248,0.18)"
                                    : "0px 2px 0px rgba(11,26,51,0.05)",
                                }}
                              >
                                {plan.popular && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: 0,
                                      right: 0,
                                      ...FREDOKA,
                                      background: "#0065F8",
                                      color: "#FFFFFF",
                                      fontWeight: 800,
                                      fontSize: 9,
                                      letterSpacing: "0.16em",
                                      textTransform: "uppercase",
                                      padding: "4px 10px",
                                      borderBottomLeftRadius: 12,
                                      boxShadow: "0px 2px 0px #073B8E",
                                    }}
                                  >
                                    Most Popular
                                  </div>
                                )}
                                <div className="flex justify-between items-center gap-3">
                                  <div>
                                    <div style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>{plan.label}</div>
                                    <div style={{ ...FREDOKA, color: "#5A6B85", fontSize: 12, marginTop: 2 }}>{plan.desc}</div>
                                  </div>
                                  <div className="text-right">
                                    <div style={{ ...FREDOKA, color: "#0065F8", fontWeight: 800, fontSize: 22, lineHeight: 1 }}>{plan.price}</div>
                                    {plan.oldPrice && (
                                      <div style={{ ...FREDOKA, color: "#94A3B8", fontWeight: 700, fontSize: 10, textDecoration: "line-through", marginTop: 2 }}>{plan.oldPrice}</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>

                      <div className="mt-5 pt-4" style={{ borderTop: "2px solid rgba(11,26,51,0.06)" }}>
                        <button
                          type="submit"
                          disabled={giftContinueDisabled}
                          className="w-full transition active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                          style={{ ...PRIMARY_PILL, width: "100%", padding: "14px 22px" }}
                        >
                          {giftSubmitting ? (
                            <>
                              <span style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.30)", borderTopColor: "#FFFFFF", animation: "settings-spin 1s linear infinite" }} />
                              Redirecting…
                            </>
                          ) : (
                            <>
                              Continue to payment
                              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                              </svg>
                            </>
                          )}
                        </button>
                        <p className="text-center mt-3 inline-flex items-center justify-center gap-1.5 w-full" style={{ ...FREDOKA, color: "#94A3B8", fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                          <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                            <rect x="3" y="11" width="18" height="11" rx="2" />
                            <path d="M7 11V7a5 5 0 0110 0v4" />
                          </svg>
                          Secure checkout · no auto-renewal
                        </p>
                      </div>
                    </section>
                  </div>
                </form>
              </div>
            )}

            {/* Account Tab */}
            {activeTab === "account" && (
              <div>
                <div className="mb-8">
                  <span style={EYEBROW}>Account</span>
                  <h2 style={TITLE}>Account &amp; Security</h2>
                  <p style={SUBTITLE}>Sign out, or close your account permanently.</p>
                </div>

                <div className="space-y-5">
                  {/* Account Info */}
                  <div style={{ ...CARD, padding: 24 }}>
                    <div className="flex items-center gap-2.5 mb-4">
                      <span style={EYEBROW}>Account Info</span>
                    </div>
                    <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Email Address</label>
                    <input
                      type="email"
                      value={user?.email || "Not available"}
                      disabled
                      style={{ ...INPUT_BASE, background: "rgba(11,26,51,0.04)", color: "#5A6B85", cursor: "not-allowed" }}
                    />
                  </div>

                  {/* Sign Out */}
                  <div className="flex items-center justify-between gap-4" style={{ ...CARD, padding: "18px 22px" }}>
                    <div className="flex items-center gap-4">
                      <div
                        className="flex-shrink-0 flex items-center justify-center"
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 14,
                          background: "linear-gradient(135deg, rgba(0,101,248,0.10) 0%, rgba(0,202,255,0.10) 100%)",
                          color: "#0065F8",
                          boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                        }}
                      >
                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
                        </svg>
                      </div>
                      <div>
                        <div style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 15 }}>Sign out</div>
                        <div style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, marginTop: 2 }}>Sign out from this device</div>
                      </div>
                    </div>
                    <button onClick={signOut} style={SECONDARY_PILL} className="transition active:scale-[0.97]">
                      Sign out
                    </button>
                  </div>

                  {/* Delete Account */}
                  <div
                    className="flex items-center justify-between gap-4"
                    style={{
                      background: "#FFFFFF",
                      border: "2px solid rgba(220,38,38,0.20)",
                      borderRadius: 24,
                      padding: "18px 22px",
                      boxShadow: "0px 4px 0px rgba(220,38,38,0.08), 0px 12px 28px rgba(11,26,51,0.06)",
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="flex-shrink-0 flex items-center justify-center"
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 14,
                          background: "rgba(220,38,38,0.10)",
                          color: "#DC2626",
                          boxShadow: "0px 2px 0px rgba(220,38,38,0.10)",
                        }}
                      >
                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" />
                        </svg>
                      </div>
                      <div>
                        <div style={{ ...FREDOKA, color: "#B91C1C", fontWeight: 700, fontSize: 15 }}>Delete account</div>
                        <div style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, marginTop: 2 }}>Permanently remove your account and all data.</div>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="transition active:scale-[0.97]"
                      style={{
                        ...FREDOKA,
                        background: "#DC2626",
                        color: "#FFFFFF",
                        fontWeight: 700,
                        fontSize: 14,
                        border: "none",
                        borderRadius: 999,
                        padding: "10px 20px",
                        boxShadow: "0px 3px 0px #991B1B",
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}
            </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Cancel flow: what they made → why → free month → confirm */}
      <CancelFlow
        isOpen={showCancelSubscriptionModal}
        onClose={() => setShowCancelSubscriptionModal(false)}
        onChanged={() => refreshSubscriptionStatus && refreshSubscriptionStatus()}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ background: "rgba(11,26,51,0.42)", backdropFilter: "blur(8px)" }}
        >
          <div
            className="w-full max-w-md"
            style={{
              ...CARD,
              border: "2px solid rgba(220,38,38,0.20)",
              padding: 28,
              boxShadow: "0px 5px 0px rgba(220,38,38,0.10), 0px 24px 64px rgba(11,26,51,0.20)",
            }}
          >
            <div
              className="mb-4 inline-flex items-center justify-center"
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "rgba(220,38,38,0.10)",
                color: "#DC2626",
              }}
            >
              <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h3 style={{ ...FREDOKA, color: "#B91C1C", fontWeight: 700, fontSize: 22, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "0 0 8px" }}>
              Delete your account?
            </h3>
            <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: "0 0 20px" }}>
              This permanently removes your characters, stories, storybooks, and settings. It can't be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isLoading}
                className="flex-1 transition active:scale-[0.98] disabled:opacity-50"
                style={{ ...SECONDARY_PILL, width: "100%", color: "#5A6B85", borderColor: "rgba(11,26,51,0.10)", padding: "12px 18px" }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isLoading}
                className="flex-1 transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ ...FREDOKA, background: "#DC2626", color: "#FFFFFF", fontWeight: 700, fontSize: 14, border: "none", borderRadius: 999, padding: "12px 18px", boxShadow: "0px 3px 0px #991B1B", cursor: "pointer" }}
              >
                {isLoading ? "Deleting…" : "Delete account"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => { setShowSubscriptionModal(false); setHighlightTier(null); }}
        apiClient={apiClient}
        user={user}
        highlightTier={highlightTier}
      />
      <HelpFooter />

      <style>{`
        @keyframes settings-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default Settings;
